import type { ReactElement } from 'react'
import {
  Analytics as AnalyticsIcon,
  BarChart as BarChartIcon,
  FaceRetouchingNatural as FaceRetouchingNaturalIcon,
  People as PeopleIcon,
  PersonSearch as PersonSearchIcon,
  School as SchoolIcon,
  Settings as SettingsIcon,
  Store as StoreIcon,
  TextSnippet as TextSnippetIcon,
} from '@mui/icons-material'

/**
 * portal navigation の SSoT.
 * 旧 PortalSidebar.tsx から抽出 (= design/16 §4.4 / M3.c Phase 2).
 *
 * Sidebar の IconBar active='settings' 時に SettingsPane が tree 描画する.
 *
 * マスタ管理グループは廃止 (log/184 v3統廃合): SOAPテンプレート / フォーマット
 * 自動挿入 / 文字列置換の旧3画面は「SOAP生成設定 > ルール」からのリンクに押し込み、
 * v3 の統合ルール画面 (/portal/soap-rules) も同セクションから飛ぶ。
 */
export interface PortalNavItem {
  id: string
  title: string
  path: string
  icon: ReactElement
  // release_channel が β 以上のユーザーにのみ表示する項目 (= β 機能。導線を隠すだけの制御)
  betaOnly?: boolean
}

export interface PortalNavGroup {
  id: string
  title: string
  icon: ReactElement
  items: PortalNavItem[]
}

export const portalNavGroups: PortalNavGroup[] = [
  {
    id: 'settings',
    title: '設定',
    icon: <SettingsIcon />,
    items: [
      {
        id: 'display-settings',
        title: '表示設定',
        path: '/portal/display-settings',
        icon: <FaceRetouchingNaturalIcon />,
      },
      {
        id: 'soap-generation',
        title: 'SOAP生成設定',
        path: '/portal/soap-generation',
        icon: <SettingsIcon />,
      },
    ],
  },
  {
    id: 'records-analytics',
    title: '記録・分析',
    icon: <AnalyticsIcon />,
    items: [
      {
        id: 'patients',
        title: '患者管理',
        path: '/portal/patients',
        icon: <FaceRetouchingNaturalIcon />,
      },
      {
        // 旧・利用統計/指導分析/指導実績一覧の統合ページ (概況/比較/明細の3タブ)
        id: 'metrics',
        title: '統計',
        path: '/portal/metrics',
        icon: <BarChartIcon />,
      },
      {
        id: 'kasan-analysis',
        title: '算定分析',
        path: '/portal/metrics/kasan',
        icon: <PersonSearchIcon />,
        betaOnly: true,
      },
    ],
  },
  {
    id: 'store-integration',
    title: '店舗・連携',
    icon: <StoreIcon />,
    items: [
      {
        // 店舗の共有資産を店長が整える面。日々の記録導線 (挿入ダイアログ) とは別の面。
        // 下ろした「定型文学習」と同じ betaOnly なので、どの店でも nav の項目数は変わらない。
        // グループ内では最も操作頻度が高いため先頭に置く。
        id: 'soap-masters',
        title: '定型文',
        path: '/portal/soap-masters',
        icon: <TextSnippetIcon />,
        betaOnly: true,
      },
      {
        id: 'pharmacists',
        title: '薬剤師管理',
        path: '/portal/pharmacists',
        icon: <PeopleIcon />,
      },
      {
        id: 'line-settings',
        title: 'LINE連携設定',
        path: '/portal/line-settings',
        icon: <SettingsIcon />,
      },
      {
        id: 'tutorial',
        title: 'チュートリアル',
        path: '/portal/tutorial',
        icon: <SchoolIcon />,
      },
    ],
  },
]

// nagi (凪): かかりつけ薬剤師の相談アバターの試作。今は mock として nagi-2 の lab
// (声 = Gemini Live・顔 = FlashHead) へ飛ばすだけで、Kakeru の患者や会話とはつながない。
// Kakeru 本体ではないので portal の nav tree には入れず、SettingsPane 最下部のリンク帯に
// β 限定で置く。lab は合言葉制なので URL を知られても中身は開かない。
export const NAGI_URL = 'https://d2uwpi2kbfkro6.cloudfront.net'
